import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import {
  fetchPnlReport,
  selectPnlData,
  selectPnlError,
  selectPnlStatus,
} from "../features/reports/reportsSlice";
import {
  fetchStores,
  selectStores,
  selectStoresStatus,
} from "../features/stores/storesSlice";

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPct(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00%";
  return `${num.toFixed(2)}%`;
}

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

function getDefaultDateRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { startDate: toIsoLocal(first), endDate: toIsoLocal(today) };
}

const STORES_CONFIG = {
  title: "Прибыльность по магазинам",
  subtitle: "Детальный отчёт P&L по магазинам",
  rowsKey: "by_store",
  labelKey: "store_name",
  labelTitle: "Магазин",
  idKey: "store_id",
  emptyText: "Нет данных по магазинам за выбранный период",
  showStoreFilter: false,
};

const CATEGORIES_CONFIG = {
  title: "Прибыльность по категориям",
  subtitle: "Детальный отчёт P&L по категориям товаров",
  rowsKey: "by_category",
  labelKey: "category_name",
  labelTitle: "Категория",
  idKey: "category_id",
  emptyText: "Нет данных по категориям за выбранный период",
  showStoreFilter: true,
};

const NUMERIC_COLUMNS = new Set([
  "revenue",
  "cogs",
  "gross_profit",
  "margin_pct",
  "sale_count",
  "refund_count",
]);

export default function AccountingPnlBreakdownPage({ kind = "stores" }) {
  const config = kind === "categories" ? CATEGORIES_CONFIG : STORES_CONFIG;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const data = useSelector(selectPnlData);
  const status = useSelector(selectPnlStatus);
  const error = useSelector(selectPnlError);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const defaultRange = useMemo(getDefaultDateRange, []);
  const [storeId, setStoreId] = useState(
    searchParams.get("store_id") || ""
  );
  const [startDate, setStartDate] = useState(
    searchParams.get("start_date") || defaultRange.startDate
  );
  const [endDate, setEndDate] = useState(
    searchParams.get("end_date") || defaultRange.endDate
  );

  const [sortBy, setSortBy] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    if (storesStatus === "idle") {
      dispatch(fetchStores({ pageSize: 200 }));
    }
  }, [dispatch, storesStatus]);

  useEffect(() => {
    dispatch(
      fetchPnlReport({
        storeId: storeId || undefined,
        startDate,
        endDate,
      })
    );
  }, [dispatch, storeId, startDate, endDate]);

  useEffect(() => {
    const params = {};
    if (storeId) params.store_id = storeId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    setSearchParams(params, { replace: true });
  }, [storeId, startDate, endDate, setSearchParams]);

  const handleApply = () => {
    dispatch(
      fetchPnlReport({
        storeId: storeId || undefined,
        startDate,
        endDate,
      })
    );
  };

  const handleReset = () => {
    setStoreId("");
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
  };

  const totals = data?.totals || {};
  const rawRows = useMemo(() => {
    const value = data?.[config.rowsKey];
    return Array.isArray(value) ? value : [];
  }, [data, config.rowsKey]);

  const sortedRows = useMemo(() => {
    const rows = [...rawRows];
    rows.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (NUMERIC_COLUMNS.has(sortBy)) {
        const an = Number(av) || 0;
        const bn = Number(bv) || 0;
        return sortDir === "desc" ? bn - an : an - bn;
      }
      const as = String(av ?? "");
      const bs = String(bv ?? "");
      return sortDir === "desc"
        ? bs.localeCompare(as, "ru")
        : as.localeCompare(bs, "ru");
    });
    return rows;
  }, [rawRows, sortBy, sortDir]);

  const totalsRow = useMemo(() => {
    return rawRows.reduce(
      (acc, row) => {
        acc.revenue += Number(row.revenue) || 0;
        acc.cogs += Number(row.cogs) || 0;
        acc.gross_profit += Number(row.gross_profit) || 0;
        acc.sale_count += Number(row.sale_count) || 0;
        acc.refund_count += Number(row.refund_count) || 0;
        return acc;
      },
      {
        revenue: 0,
        cogs: 0,
        gross_profit: 0,
        sale_count: 0,
        refund_count: 0,
      }
    );
  }, [rawRows]);

  const totalsRowMargin =
    totalsRow.revenue > 0
      ? (totalsRow.gross_profit / totalsRow.revenue) * 100
      : 0;

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const backQs = useMemo(() => {
    const params = new URLSearchParams();
    if (storeId) params.set("store_id", String(storeId));
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [storeId, startDate, endDate]);

  const columns = [
    { key: config.labelKey, title: config.labelTitle, sortable: true },
    { key: "revenue", title: "Выручка", money: true, sortable: true },
    { key: "cogs", title: "Себестоимость", money: true, sortable: true },
    {
      key: "gross_profit",
      title: "Прибыль",
      money: true,
      bold: true,
      sortable: true,
    },
    { key: "margin_pct", title: "Маржа", pct: true, sortable: true },
    { key: "sale_count", title: "Продаж", numeric: true, sortable: true },
    { key: "refund_count", title: "Возвратов", numeric: true, sortable: true },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <Link to={`/accounting/pnl${backQs}`} style={styles.backLink}>
            ← Назад к P&L
          </Link>
          <div style={styles.title}>{config.title}</div>
          <div style={styles.subtitle}>{config.subtitle}</div>
        </div>
      </div>

      <div style={styles.toolbar}>
        {config.showStoreFilter && (
          <div style={styles.field}>
            <label style={styles.label}>Магазин</label>
            <Select
              value={storeId}
              onChange={setStoreId}
              options={[
                { value: "", label: "Все магазины" },
                ...stores.map((s) => ({
                  value: String(s.id),
                  label: s.name || `Магазин #${s.id}`,
                })),
              ]}
              isSearchable={false}
            />
          </div>
        )}
        <div style={styles.field}>
          <label style={styles.label}>С даты</label>
          <DatePicker
            selected={parseIsoLocal(startDate)}
            onChange={(d) => setStartDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input style={styles.input} />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>По дату</label>
          <DatePicker
            selected={parseIsoLocal(endDate)}
            onChange={(d) => setEndDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input style={styles.input} />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div style={styles.toolbarButtons}>
          <button type="button" onClick={handleApply} style={styles.applyBtn}>
            Обновить
          </button>
          <button type="button" onClick={handleReset} style={styles.resetBtn}>
            Сбросить
          </button>
        </div>
      </div>

      {status === "loading" && (
        <Loading text="Загрузка отчёта…" />
      )}
      {status === "failed" && error && (
        <div style={styles.errorBox}>{error}</div>
      )}

      <div style={styles.body}>
        <div style={styles.kpiGrid}>
          <KpiCard
            title="Выручка"
            value={`${formatMoney(totals.revenue)} сом`}
            hint={`Продажи: ${formatMoney(totals.sale_revenue)}`}
          />
          <KpiCard
            title="Себестоимость"
            value={`${formatMoney(totals.cogs)} сом`}
            hint={`COGS продаж: ${formatMoney(totals.sale_cogs)}`}
          />
          <KpiCard
            title="Валовая прибыль"
            value={`${formatMoney(totals.gross_profit)} сом`}
            hint={`Возвратов: ${formatMoney(totals.refund_revenue)}`}
            accent="var(--c-success)"
          />
          <KpiCard
            title="Маржа"
            value={formatPct(totals.margin_pct)}
            hint={`${totals.sale_count || 0} продаж · ${totals.refund_count || 0} возвратов`}
            accent="var(--c-text)"
          />
        </div>

        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>
              Детальная разбивка ({sortedRows.length} строк)
            </div>
            <div style={styles.sectionHint}>
              Кликните по заголовку столбца для сортировки
            </div>
          </div>
          {sortedRows.length === 0 ? (
            <div style={styles.empty}>{config.emptyText}</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        style={{
                          ...styles.th,
                          ...(c.money || c.pct || c.numeric
                            ? { textAlign: "right" }
                            : null),
                          cursor: c.sortable ? "pointer" : "default",
                          userSelect: "none",
                        }}
                        onClick={
                          c.sortable ? () => toggleSort(c.key) : undefined
                        }
                      >
                        <span style={{ display: "inline-flex", gap: 4 }}>
                          {c.title}
                          {sortBy === c.key && (
                            <span style={{ color: "var(--c-text)" }}>
                              {sortDir === "desc" ? "▼" : "▲"}
                            </span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, idx) => (
                    <tr
                      key={`${row[config.idKey] ?? "x"}-${idx}`}
                      style={styles.tr}
                    >
                      {columns.map((c) => {
                        const raw = row[c.key];
                        let display = raw ?? "—";
                        if (c.money) display = `${formatMoney(raw)} сом`;
                        else if (c.pct) display = formatPct(raw);
                        else if (c.numeric)
                          display = Number(raw || 0).toLocaleString("ru-RU");
                        return (
                          <td
                            key={c.key}
                            style={{
                              ...styles.td,
                              ...(c.bold ? { fontWeight: 800 } : null),
                              ...(c.money || c.pct || c.numeric
                                ? {
                                    textAlign: "right",
                                    fontVariantNumeric: "tabular-nums",
                                  }
                                : { fontWeight: 700 }),
                            }}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={styles.totalsRow}>
                    <td style={{ ...styles.tdTotals, fontWeight: 800 }}>
                      Итого
                    </td>
                    <td style={styles.tdTotalsNum}>
                      {formatMoney(totalsRow.revenue)} сом
                    </td>
                    <td style={styles.tdTotalsNum}>
                      {formatMoney(totalsRow.cogs)} сом
                    </td>
                    <td
                      style={{
                        ...styles.tdTotalsNum,
                        fontWeight: 800,
                        color: "var(--c-success)",
                      }}
                    >
                      {formatMoney(totalsRow.gross_profit)} сом
                    </td>
                    <td
                      style={{ ...styles.tdTotalsNum, color: "var(--c-text)" }}
                    >
                      {formatPct(totalsRowMargin)}
                    </td>
                    <td style={styles.tdTotalsNum}>
                      {Number(totalsRow.sale_count).toLocaleString("ru-RU")}
                    </td>
                    <td style={styles.tdTotalsNum}>
                      {Number(totalsRow.refund_count).toLocaleString("ru-RU")}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ title, value, hint, accent }) {
  return (
    <div style={styles.kpiCard}>
      <div style={styles.kpiTitle}>{title}</div>
      <div style={{ ...styles.kpiValue, color: accent || "var(--c-text)" }}>
        {value}
      </div>
      {hint && <div style={styles.kpiHint}>{hint}</div>}
    </div>
  );
}

const styles = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--c-bg)",
    color: "var(--c-text)",
    overflow: "auto",
  },
  header: {
    padding: "16px 24px",
    borderBottom: "1px solid var(--c-border)",
    background: "var(--c-bg)",
    flexShrink: 0,
  },
  backLink: {
    display: "inline-block",
    marginBottom: 8,
    fontSize: 14,
    color: "var(--c-text-muted)",
    textDecoration: "none",
    fontWeight: 600,
  },
  title: { fontSize: 22, fontWeight: 900, color: "var(--c-text)", margin: 0 },
  subtitle: { fontSize: 15, color: "var(--c-text-muted)", marginTop: 3 },
  toolbar: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    padding: "12px 24px",
    borderBottom: "1px solid var(--c-border)",
    background: "var(--c-bg)",
    flexShrink: 0,
  },
  field: { display: "flex", flexDirection: "column", gap: 4, minWidth: 160 },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--c-text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  input: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  select: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    cursor: "pointer",
    minWidth: 200,
  },
  toolbarButtons: { display: "flex", gap: 8, marginLeft: "auto" },
  applyBtn: {
    padding: "9px 18px",
    borderRadius: 99,
    border: "none",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  resetBtn: {
    padding: "9px 16px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 15,
  },
  notice: { padding: "14px 24px", fontSize: 15, color: "var(--c-text-muted)" },
  errorBox: {
    margin: "0 24px",
    padding: "12px 16px",
    background: "rgba(248,113,113,0.1)",
    border: "1px solid rgba(248,113,113,0.2)",
    borderRadius: 16,
    color: "var(--c-danger)",
    fontSize: 15,
  },
  body: {
    padding: "20px 24px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  kpiCard: {
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  kpiTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--c-text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  kpiValue: {
    fontSize: 23,
    fontWeight: 900,
    color: "var(--c-text)",
    fontVariantNumeric: "tabular-nums",
  },
  kpiHint: { fontSize: 14, color: "var(--c-text-muted)" },
  section: { display: "flex", flexDirection: "column", gap: 10 },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 8,
    flexWrap: "wrap",
  },
  sectionTitle: { fontSize: 16, fontWeight: 800, color: "var(--c-text)" },
  sectionHint: { fontSize: 14, color: "var(--c-text-muted)" },
  tableWrap: {
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    overflow: "hidden",
    background: "var(--c-card)",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    textAlign: "left",
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 700,
    color: "var(--c-text-sub)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  tr: {},
  td: {
    padding: "11px 14px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
    color: "var(--c-text)",
  },
  totalsRow: { background: "var(--c-input)" },
  tdTotals: {
    padding: "12px 14px",
    fontSize: 15,
    color: "var(--c-text)",
    borderTop: "2px solid var(--c-border)",
  },
  tdTotalsNum: {
    padding: "12px 14px",
    fontSize: 15,
    color: "var(--c-text)",
    borderTop: "2px solid var(--c-border)",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontWeight: 700,
  },
  empty: {
    padding: "24px",
    textAlign: "center",
    fontSize: 15,
    color: "var(--c-text-muted)",
    border: "1px dashed var(--c-border)",
    borderRadius: 12,
    background: "var(--c-card)",
  },
};
